<script lang="ts" setup>
import { useBreakpoints, useIntervalFn } from '@vueuse/core';
import { computed, ref, watch, type PropType } from 'vue';
import { VTooltip, VIcon } from 'vuetify/components';

import BigNumber from '@exchange/helpers/bignumber';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { useFundingRate } from '@exchange/libs/funding-rate/service/src';
import type { FundingRateData } from '@exchange/libs/funding-rate/service/src/lib/funding-rate-model.js';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketType } from '@exchange/libs/market/service/src/lib/market-model';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import type { CurrencyModel } from '@exchange/libs/utils/currency/src';

const props = defineProps({
  currencies: { type: Object as PropType<Dictionary<CurrencyModel>> },
  defaultFiatCurrency: { type: String, required: true },
  market: { type: Object as PropType<MarketModel> },
});
const { isSMAndDown } = useMQBreakpoints();
const defaultPrecision = {
  percentage: CONSTANTS.PRECISION.DEFAULT_PERCENTAGE,
  zero: CONSTANTS.PRECISION.ZERO,
};

const fiatVolume = computed(() => {
  if (!props.market || !props.market.allDay.globalVolume || !props.currencies || !props.currencies[props.market.quote.code]?.defaultFiatValue) {
    return Number.NaN;
  }

  return new BigNumber(props.market.allDay.globalVolume).times(props.currencies[CONSTANTS.DEFAULT_FOR_CURRENCY_FORMATTING]?.defaultFiatValue ?? 0);
});
const priceChangeChecked = ref(false);
const priceIncreased = ref(false);
const lastCheckedMarket = ref(props.market?.name || '');

watch(
  () => props.market?.lastPrice,
  (newVal, oldVal) => {
    if (lastCheckedMarket.value === props.market?.name) {
      if (newVal && oldVal && newVal !== oldVal) {
        priceChangeChecked.value = true;
        priceIncreased.value = newVal > oldVal;
      }
    } else {
      priceChangeChecked.value = false;
      lastCheckedMarket.value = props.market?.name || '';
    }
  },
);

const fundingRateData = ref<FundingRateData | null>(null);
const { fundingRate, isLoading: isFundingRateLoading } = useFundingRate(computed(() => props.market?.id || ''));

watch(
  [fundingRate, () => props.market?.type],
  ([newVal, marketType]) => {
    if (marketType === MarketType.PERP && newVal) {
      fundingRateData.value = newVal;
    }
  },
  { immediate: true },
);

const getNextFundingTime = () => {
  const now = new Date();
  // Round down to the start of the current hour
  now.setMinutes(0, 0, 0);

  // Get the next 4-hour interval
  const currentHour = now.getUTCHours();
  const nextInterval = Math.ceil((currentHour + 1) / 4) * 4;

  const next = new Date(now);
  next.setUTCHours(nextInterval, 0, 0, 0);

  // If we're already past the calculated time, add 4 hours
  if (next <= now) {
    next.setTime(next.getTime() + 4 * 60 * 60 * 1000);
  }

  return next;
};

const nextFundingTime = ref(getNextFundingTime());

const countdown = ref('');

const updateCountdown = () => {
  const now = new Date();
  const diff = nextFundingTime.value.getTime() - now.getTime();

  if (diff <= 0) {
    // Reset the next funding time
    nextFundingTime.value = getNextFundingTime();
    updateCountdown();
    return;
  }

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  countdown.value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Update countdown every second
useIntervalFn(updateCountdown, 1000);

const isFutures = computed(() => props.market?.type === MarketType.PERP);

const getTooltip = (d: { message: string; html?: boolean }) => ({
  content: d.message,
  placement: 'bottom',
  html: d.html,
});
</script>

<template>
  <div class="market-header-details">
    <!--  current price-->
    <div class="market-header-details__item current-price">
      <span class="market-header-details__label">{{ $t('modules.marketDetails.currentPrice') }}</span>
      <ot-number-display
        v-if="market"
        class="market-header-details__value"
        :class="{ [`market-header-details__last-price-${priceIncreased ? 'arrow-up' : 'arrow-down'}`]: priceChangeChecked }"
        :grouping="true"
        :left-aligned="true"
        :number="market.lastPrice"
        :precision="market.marketPrecision"
        :smaller-suffix="true"
        :suffix="market.quote.code"
      />
      <div
        v-else
        class="market-header-details__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
    <!--  24h price 24-change-->
    <div class="market-header-details__item h24-change">
      <span class="market-header-details__label">{{ $t('modules.marketDetails.24hPrice') }}</span>
      <ot-number-display
        v-if="market"
        class="market-header-details__value"
        :add-plus-sign="true"
        :colored-percentage="true"
        :left-aligned="true"
        :number="market.priceChangePercentageToDisplay"
        :percentage="true"
        :precision="defaultPrecision.percentage"
      />
      <div
        v-else
        class="market-header-details__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
    <!--  24h high-->
    <div
      v-if="!isFutures"
      class="market-header-details__item h24-height"
    >
      <span class="market-header-details__label">{{ $t('modules.marketDetails.24hHigh') }}</span>
      <ot-number-display
        v-if="market"
        class="market-header-details__value"
        suffix-class="market-header-details__value-suffix"
        :grouping="true"
        :left-aligned="true"
        :number="market.allDay.high"
        :precision="market.marketPrecision"
        :smaller-suffix="true"
        :suffix="market.quote.code"
      />
      <div
        v-else
        class="market-header-details__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
    <!--  24h low-->
    <div
      v-if="!isFutures"
      class="market-header-details__item h24-low"
    >
      <span class="market-header-details__label">{{ $t('modules.marketDetails.24hLow') }}</span>
      <ot-number-display
        v-if="market"
        class="market-header-details__value"
        suffix-class="market-header-details__value-suffix"
        :grouping="true"
        :left-aligned="true"
        :number="market.allDay.low"
        :precision="market.marketPrecision"
        :smaller-suffix="true"
        :suffix="market.quote.code"
      />
      <div
        v-else
        class="market-header-details__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
    <!--  24h volume-->
    <div
      v-if="!isSMAndDown"
      class="market-header-details__item h24-volume"
    >
      <div class="market-header-details__label-group">
        <span class="market-header-details__label">
          {{ $t('modules.marketDetails.24hVolume') }}
          <v-tooltip
            :text="$t('modules.marketDetails.globalVolumeTooltip')"
            location="bottom"
          >
            <template #activator="{ props }">
              <ot-icon
                v-bind="props"
                class="market-header-details__info-icon"
                name="info-circle"
              />
            </template>
          </v-tooltip>
        </span>
      </div>
      <ot-number-display
        v-if="market"
        class="market-header-details__value"
        suffix-class="market-header-details__value-suffix"
        :grouping="true"
        :left-aligned="true"
        :number="fiatVolume"
        :precision="defaultPrecision.zero"
        :smaller-suffix="true"
        :suffix="defaultFiatCurrency"
      />
      <div
        v-else
        class="market-header-details__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
    <!-- funding rate (only for futures) -->
    <div
      v-if="isFutures"
      class="market-header-details__item funding-rate"
    >
      <span class="market-header-details__label">Funding / Countdown</span>
      <template v-if="!isFundingRateLoading && fundingRateData && countdown">
        <div class="market-header-details__value-group">
          <ot-number-display
            class="market-header-details__value"
            suffix-class="market-header-details__value-suffix"
            :add-plus-sign="false"
            :colored-percentage="true"
            :left-aligned="true"
            :number="Number(fundingRateData.funding_rate) * 100"
            :percentage="true"
            :precision="4"
          />
          /
          <span class="market-header-details__countdown">{{ countdown }}</span>
        </div>
      </template>
      <div
        v-else
        class="market-header-details__value"
      >
        {{ $t('fundamentals.emptyValues.default') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
/* stylelint-disable */
.market-header-details {
  display: flex;
  gap: 30px;

  .market-header-details {
    &__item {
      display: flex;
      min-width: min-content;
      flex-direction: column;
      justify-content: center;
    }

    &__label {
      color: rgb(var(--v-theme-text-secondary));
      font-size: var(--font-size-xxs);
      line-height: 1;
      white-space: nowrap;
    }

    &__value {
      color: rgb(var(--v-theme-text-primary));
      font-size: var(--font-size-md);
    }

    &__value-suffix {
      color: rgb(var(--v-theme-text-secondary));
    }

    &__last-price-up {
      color: rgb(var(--v-theme-success));
    }

    &__last-price-down {
      color: rgb(var(--v-theme-error));
    }

    &__value-group {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__countdown {
      color: rgb(var(--v-theme-text-secondary));
      font-size: var(--font-size-md);
    }

    &__label-group {
      display: flex;
      align-items: center;
      gap: 4px;
    }

    &__info-icon {
      height: 16px;
      width: 16px;
      vertical-align: text-bottom;
    }
  }
}
</style>
